import { invalidate } from '@react-three/fiber';
import { t } from 'i18next';
import { Box3, Matrix4, Plane, Vector3 } from 'three';

import { SettingModelSettingType } from 'api/models';
import { GroupType } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupType';
import {
    clearHoveredHighlight,
    cloneArticleInstances,
    getArticleId,
    getArticleInstanceData,
    HoverArticleGroupEntry,
    hoveredGroupByArticle,
    hoveredGroupInstanceIds,
    isAnchorArticle,
    isSupportingConstruction,
    isFramiWedgeClamp,
    resolveHoverArticleRef
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    alignSelectionToCursorPoint,
    anchorPosition,
    appendGroupedInstance,
    applyMoveToInstance,
    applySnapOffsetToInstances,
    computePerInstanceWallDelta,
    dragAnchorPositionRef,
    dragColliderCache,
    draggedSnapRelationIds,
    syncDraggedSnapRelationIds,
    dragDelta,
    draggingBoxStatus,
    getAnchorAlignment,
    getAnchorDragContext,
    getAnchorPosition,
    getConnectorMainPartName,
    alignAdjustableConnectorPartsToSnapTarget,
    normalizeAdjustableConnectorSnapTarget,
    groupSelectedInstancesByArticle,
    handleDragSettings,
    handleMarkerVisibility,
    hasAnyRegisteredDragRefs,
    instanceToArticleIdMap,
    instanceToPoiIndexMap,
    isMixedAlignmentSelection,
    isTopBottomCameraView,
    perInstanceDragOffsets,
    perInstancePrevIntersections,
    perInstancePlanes,
    projectDeltaToWorkingPlane,
    raycastPlane,
    scheduleDraggingBoxComputation,
    targetPosition,
    translateDraggingBox,
    updateInstanceToArticleIdMap,
    updateInstanceToPoiIndexMap,
    initializePerInstancePlanesAndIntersections,
    placeInstanceOnWall,
    initialPoint,
    getConnectorArticleId,
    getManualPlacementPlane,
    getConnectorSnapSource,
    findNearestConnectorSnapTarget,
    getAdditionalConnectorSnapFeedback,
    getConnectorSnapCaptureDistance,
    createConnectorSnapPlane,
    applyAlignmentToSelectedInstances,
    applyRotationToSelectedInstances,
    adjustedSnapSource,
    getPreferredInsertionPlane,
    refreshInstanceColliderCache,
    shouldUseZAxisOnlyDrag,
    activePoiGuids,
    retargetNormal,
    retargetProjectedPoint,
    updateCornerPlacementTarget,
    updateWallPlacementTarget,
    updateCursorRay
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { getPoiIndexForArticleInstance } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { inputPointerPosition } from 'app/modules/wallforming/components/scenes/objects/utils/pointerUtils';
import {
    CONNECTOR_SNAP_CAPTURE_RADIUS_PX,
    getSnapCaptureDistance,
    SNAP_PREVIEW_DISTANCE_FACTOR
} from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { yieldToMain } from 'app/modules/wallforming/components/state-management/placement/utils/schedulerUtils';
import {
    DRAG_START_THRESHOLD,
    DRAGGING_BOX_THRESHOLD,
    EPSILON,
    MATRIX_UPDATE_CHUNK_SIZE,
    SNAP_THROTTLE_MS
} from 'shared/constants';

import type { ArticleDragReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleDragReference';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';
import type { GroupMember } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupMember';
import type { SnapPointFeedback } from 'app/modules/wallforming/components/scenes/objects/helpers/types/snapPointFeedback';
import type { WallEdge } from 'app/modules/wallforming/components/scenes/objects/helpers/types/wallEdge';
import type { ArticleEventHandlerSliceState } from 'app/modules/wallforming/components/state-management/placement/states/articleEventHandlerSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { RefObject } from 'react';
import type { StateCreator } from 'zustand';

const pointerDownScreen = { x: 0, y: 0 };
let isDragEnabled = false;

// Alignment state for the current drag session.
let isMixedAlignmentDrag = false;

let isGroupDragActive = false;
const groupMemberIds = new Set<string>();

const sharedAlignmentNormal = new Vector3();
let isTopBottomViewDrag = false;

const snapOffset = new Vector3();
const planeIntersectPoint = new Vector3();

// Reusable vectors for per-instance wall projection during mixed-alignment drag.
const perInstanceDelta = new Vector3();
// Reusable vector for projecting snap offset onto per-instance wall planes.
const snapProjDelta = new Vector3();
// Captures the anchor's actual wall-plane movement delta for dragging-box sync.
const anchorMovedDelta = new Vector3();
const snapFeedbackTargetsByCell = new Map<string, Vector3[]>();
// Per-tick preview snap results of dragged instances; capture passes are skipped for instances without a hit.
const previewSnapByInstance = new Map<string, Vector3[]>();

function snapFeedbackCellKey(x: number, y: number, z: number): string {
    return `${x},${y},${z}`;
}

function isDuplicateSnapFeedbackTarget(target: Vector3): boolean {
    const cellX = Math.floor(target.x / EPSILON);
    const cellY = Math.floor(target.y / EPSILON);
    const cellZ = Math.floor(target.z / EPSILON);

    for (let x = cellX - 1; x <= cellX + 1; x++) {
        for (let y = cellY - 1; y <= cellY + 1; y++) {
            for (let z = cellZ - 1; z <= cellZ + 1; z++) {
                const nearbyTargets = snapFeedbackTargetsByCell.get(snapFeedbackCellKey(x, y, z));
                if (nearbyTargets?.some((existing) => existing.distanceToSquared(target) <= EPSILON * EPSILON)) {
                    return true;
                }
            }
        }
    }

    return false;
}

function addSnapFeedbackTarget(target: Vector3): void {
    const key = snapFeedbackCellKey(
        Math.floor(target.x / EPSILON),
        Math.floor(target.y / EPSILON),
        Math.floor(target.z / EPSILON)
    );
    const targetsInCell = snapFeedbackTargetsByCell.get(key);
    if (targetsInCell) {
        targetsInCell.push(target);
    } else {
        snapFeedbackTargetsByCell.set(key, [target]);
    }
}

const instancesByArticleId = new Map<string, string[]>();

let snapAnchorInstanceId: string | undefined;

const dragStartPoint = new Vector3();
let hasDragStartPoint = false;

let lastSnapTime = 0;
let cachedSnapPoints: Vector3[] | null = null;
let isCornerSnapActive = false;

let hasCachedSnapAnchorPosition = false;
let hasDragStartReferenceOffset = false;

const worldCorner = new Vector3();
const projectedCorner = new Vector3();
const cachedIntersect = new Vector3();

const cachedSnapAnchorPosition = new Vector3();
const currentSnapAnchorPosition = new Vector3();

const dragStartReferenceOffset = new Vector3();
const lastCursorPlanePoint = new Vector3();

let hasLastCursorPlanePoint = false;

let isDragStart = true;
let hasPreviousDragPoint = false;

let lastSnappedEdge: WallEdge | null = null;
let connectorSnapCandidateIndex: number | null = null;
let connectorSnapWallPoiId: string | null = null;
let isManualInsertionPendingSnap = false;
let manualSnapSourceInstanceId: string | null = null;

function getActiveManualSettingType(
    state: PlacementState,
    articleId: string | null | undefined
): SettingModelSettingType | undefined {
    return articleId && state.manualPlacementData.articleId === articleId
        ? state.manualPlacementData.settingType
        : undefined;
}

function isSolverGuidedSettingType(settingType: SettingModelSettingType | undefined): boolean {
    return settingType === SettingModelSettingType.Connectors || settingType === SettingModelSettingType.Anchors;
}

// A manually snapped wedge clamp joins its support's clamp group, or forms a new automatic group with it.
function joinSupportClampGroup(state: PlacementState, supportInstanceId: string, clamp: GroupMember): void {
    const supportArticleId = getArticleId(supportInstanceId, state);
    if (!supportArticleId || !isSupportingConstruction(state.articles[supportArticleId]?.articleNumber ?? null)) {
        return;
    }

    const isWedgeClampMember = (member: GroupMember) =>
        isFramiWedgeClamp(state.articles[member.articleId]?.articleNumber ?? null);
    const supportGroup = state.getGroupByInstanceId(supportInstanceId);
    if (
        supportGroup?.members.some(isWedgeClampMember) &&
        supportGroup.members.every((member) => member.instanceId === supportInstanceId || isWedgeClampMember(member))
    ) {
        state.updateArticleGroup(
            supportGroup.id,
            supportGroup.name,
            [...supportGroup.members, clamp],
            supportGroup.color
        );
        return;
    }

    state.createArticleGroup(
        t('labelAutoPlaceSupportingConstructionClamps'),
        [{ articleId: supportArticleId, instanceId: supportInstanceId }, clamp],
        undefined,
        GroupType.AUTOMATIC
    );
}

export function syncDragStateOnPoiChange(selectedInstanceIds: string[], targetPlane: Plane): void {
    targetPosition.copy(retargetProjectedPoint);
    initialPoint.copy(retargetProjectedPoint);
    planeIntersectPoint.copy(retargetProjectedPoint);
    lastCursorPlanePoint.copy(retargetProjectedPoint);
    hasLastCursorPlanePoint = true;
    hasPreviousDragPoint = true;

    sharedAlignmentNormal.copy(retargetNormal);
    isMixedAlignmentDrag = false;
    isTopBottomViewDrag = isTopBottomCameraView(raycastPlane.normal);

    perInstancePlanes.clear();
    perInstancePrevIntersections.clear();
    for (const instanceId of selectedInstanceIds) {
        perInstancePlanes.set(instanceId, targetPlane.clone());
        perInstancePrevIntersections.set(instanceId, retargetProjectedPoint.clone());
    }
}

function resetDragSettings(): void {
    snapAnchorInstanceId = undefined;
    draggingBoxStatus.isPending = false;
    draggingBoxStatus.isComputed = false;

    cachedSnapPoints = null;
    isCornerSnapActive = false;
    hasCachedSnapAnchorPosition = false;
    hasDragStartReferenceOffset = false;
    hasDragStartPoint = false;
    lastSnapTime = 0;
    hasPreviousDragPoint = false;
    isDragEnabled = false;
    isMixedAlignmentDrag = false;
    sharedAlignmentNormal.set(0, 0, 0);
    isTopBottomViewDrag = false;
    instanceToPoiIndexMap.clear();
    dragColliderCache.clear();
    draggedSnapRelationIds.clear();
    previewSnapByInstance.clear();
    perInstanceDragOffsets.clear();
    perInstancePlanes.clear();
    perInstancePrevIntersections.clear();
    dragAnchorPositionRef.current = null;
    activePoiGuids.clear();
    lastSnappedEdge = null;
    connectorSnapCandidateIndex = null;
    connectorSnapWallPoiId = null;
    isManualInsertionPendingSnap = false;
    manualSnapSourceInstanceId = null;
}

export const createArticleEventHandlerSlice: StateCreator<PlacementState, [], [], ArticleEventHandlerSliceState> = (
    set,
    get
) => ({
    recomputeTransformationBox: false,
    toggleRecomputeTransformationBox: () =>
        set((state) => ({ recomputeTransformationBox: !state.recomputeTransformationBox })),

    hoveredArticleInstanceIds: [],
    setHoveredArticleInstanceIds: (instanceIds) => set({ hoveredArticleInstanceIds: instanceIds }),

    isArticleDirectlySelectedRef: { current: false },
    setIsArticleDirectlySelectedRef: (isSelected) => {
        const state = get();
        if (state.isArticleDirectlySelectedRef.current !== isSelected) {
            state.isArticleDirectlySelectedRef.current = isSelected;
        }
    },

    draggingBoxRef: {
        current: {
            isActive: false,
            boundingBox: new Box3(),
            localToWorld: new Matrix4()
        }
    },

    articleDragRefsMap: { current: new Map<string, ArticleDragReference>() },
    getSelectedArticleDragReference: (articleInstanceId) => {
        const state = get();
        const instanceId = articleInstanceId ?? Object.keys(state.articleInstanceSelections)[0];
        if (!instanceId) {
            return undefined;
        }
        let articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            // lazily resolve from state.articles (e.g. after box selection)
            articleId = Object.keys(state.articles).find((id) => instanceId in state.articles[id].articleInstances);
            if (articleId) {
                instanceToArticleIdMap.set(instanceId, articleId);
            }
        }
        if (!articleId) {
            return undefined;
        }
        return state.articleDragRefsMap.current.get(articleId);
    },

    registerArticleDragReference: (articleId, articleRef) => {
        const state = get();
        state.articleDragRefsMap.current.set(articleId, { articleRef });
        if (state.isAutoDragActiveRef.current) {
            invalidate();
        }

        // Fallback: update z position when dragging got interrupted during top/bottom view insertion
        const isDragRefRegistered = state.articleDragRefsMap.current.get(articleId ?? '') !== undefined;
        if (state.isZPositionUpdatePending && isDragRefRegistered) {
            const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
            if (selectedInstanceIds.length === 1) {
                placeInstanceOnWall(selectedInstanceIds[0], articleId, state, state.articleDragRefsMap.current);
                state.setIsZPositionUpdatePending(false);
            }
        }
    },
    unregisterArticleDragReference: (articleId) => {
        get().articleDragRefsMap.current.delete(articleId);
    },

    handleOnInsertArticleStart: (isDoubleClick) => {
        const state = get();
        if (state.isViewMode) {
            return;
        }
        if (!isDoubleClick && state.manualArticleInsertionButtonRef.current === null) {
            return;
        }
        if (state.arePivotControlsActive) {
            state.updatePivotControls();
        }

        if (state.isPanelQuickReplacementOpen) {
            state.setIsPanelQuickReplacementOpen(false);
        }

        state.setArticleInstanceSelections({});

        if (isDoubleClick) {
            state.resetHiddenPOIFilterTypes();
            state.setIsAutoDragActiveRef(true);
        }

        // Reset cursor plane point from a previous drag session
        hasLastCursorPlanePoint = false;
        state.setIsDragNewArticleStartRef(true);
    },
    manualArticleInsertionButtonRef: { current: null },
    beginManualArticleInsertionDrag: (clientX, clientY, button) => {
        if (get().isViewMode) {
            return;
        }
        pointerDownScreen.x = clientX;
        pointerDownScreen.y = clientY;
        isDragEnabled = false;
        get().manualArticleInsertionButtonRef.current = button;
    },
    cancelManualArticleInsertionDrag: () => {
        const state = get();
        if (!state.isDragNewArticleActiveRef.current) {
            state.setIsDragNewArticleStartRef(false);
        }
        state.manualArticleInsertionButtonRef.current = null;
        isDragEnabled = false;
    },

    updateAutoDragTarget: async (filterType, index) => {
        const state = get();
        if (state.isViewMode) {
            return false;
        }
        const currentFilterType = state.poiSelection.current.filterType;
        if (!state.isAutoDragActiveRef.current) {
            return true;
        }

        if (filterType === undefined || index === undefined) {
            return false;
        }

        if (currentFilterType !== undefined && filterType !== currentFilterType) {
            return false;
        }

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);

        switch (filterType) {
            case POIFilterType.Wall:
                return updateWallPlacementTarget(state, selectedInstanceIds, index, snapAnchorInstanceId);
            case POIFilterType.Corner:
                return updateCornerPlacementTarget(state, get, selectedInstanceIds, index);
            default:
                return false;
        }
    },

    syncSelectedInstancesToCursor: () => {
        const state = get();
        if (
            state.isViewMode ||
            !state.isAutoDragActiveRef.current ||
            state.isDragNewArticleStartRef.current ||
            !updateCursorRay(state)
        ) {
            return false;
        }

        const ray = state.mainRaycasterRef.current?.ray;
        if (!ray) {
            return false;
        }

        state.handleOnDragArticle({
            buttons: 0,
            clientX: inputPointerPosition.x,
            clientY: inputPointerPosition.y,
            altKey: inputPointerPosition.altKey,
            ray
        });
        return true;
    },

    handleOnDragArticle: (event) => {
        let state = get();
        if (state.isTransformationActiveRef.current || state.isViewMode) {
            return;
        }

        // Skip dragging if manual placement data is loading
        if (get().waitForPlacementData) {
            return;
        }

        const pointerArticleId =
            getConnectorArticleId(state, snapAnchorInstanceId) ??
            (snapAnchorInstanceId ? getArticleId(snapAnchorInstanceId, state) : null) ??
            null;
        const pointerManualInsertionPlane = getManualPlacementPlane(state, pointerArticleId);
        const preferredPointerPlane =
            (state.isDragNewArticleStartRef.current ||
                state.isDragNewArticleActiveRef.current ||
                state.isAutoDragActiveRef.current) &&
            getPreferredInsertionPlane(state, pointerManualInsertionPlane);

        if (preferredPointerPlane && event.ray.intersectPlane(preferredPointerPlane, planeIntersectPoint)) {
            lastCursorPlanePoint.copy(planeIntersectPoint);
            hasLastCursorPlanePoint = true;
        } else if (event.ray.intersectPlane(raycastPlane, planeIntersectPoint)) {
            lastCursorPlanePoint.copy(planeIntersectPoint);
            hasLastCursorPlanePoint = true;
        }

        const manualInsertionButton = state.manualArticleInsertionButtonRef.current;
        const requiredButtonsMask =
            manualInsertionButton === null ? 1 : manualInsertionButton === 0 ? 1 : manualInsertionButton === 1 ? 4 : 2;
        if ((event.buttons & requiredButtonsMask) === 0 && !get().isAutoDragActiveRef.current) {
            return;
        }

        // update and check drag start threshold
        if (!isDragEnabled && !state.isAutoDragActiveRef.current) {
            const dx = event.clientX - pointerDownScreen.x;
            const dy = event.clientY - pointerDownScreen.y;
            if (dx * dx + dy * dy > DRAG_START_THRESHOLD * DRAG_START_THRESHOLD) {
                isDragEnabled = true;
            } else {
                return;
            }
        }

        if (!state.autoPlaceSupportingConstructionClamps && !state.isDragNewArticleActiveRef.current) {
            for (const instanceId of Object.keys(state.articleInstanceSelections)) {
                const articleId = getArticleId(instanceId, state);
                const articleNumber = articleId ? state.articles[articleId]?.articleNumber : null;
                const groupId = state.instanceToGroupMap[instanceId];
                if (isSupportingConstruction(articleNumber) && groupId) {
                    state.removeArticleGroup(groupId);
                }
            }
        }

        if (get().isArticleDirectlySelectedRef.current && !get().isDragActiveRef.current) {
            get().setIsDragActiveRef(true);
        }

        let isAnchorInsertedThisEvent = false;
        if (get().isDragNewArticleStartRef.current) {
            const selectedArticleId = state.selectedArticleId;
            const isConnectorInsertionRequested = isSolverGuidedSettingType(
                getActiveManualSettingType(state, selectedArticleId)
            );

            if (
                isConnectorInsertionRequested &&
                (state.manualPlacementData.isLoading ||
                    state.manualPlacementData.candidates.length === 0 ||
                    state.manualPlacementData.plane === null)
            ) {
                return;
            }

            const insertionManualPlane = getManualPlacementPlane(state, state.selectedArticleId);
            const insertionPlane = getPreferredInsertionPlane(state, insertionManualPlane);
            let insertionPosition: Vector3 | undefined;
            if (insertionPlane && event.ray.intersectPlane(insertionPlane, planeIntersectPoint)) {
                insertionPosition = planeIntersectPoint.clone();
            } else if (hasLastCursorPlanePoint) {
                insertionPosition = lastCursorPlanePoint;
            }

            get().insertArticleInstance(insertionPosition);
            state = get();
            const insertedSettingType = getActiveManualSettingType(state, selectedArticleId);
            const hasInsertedSelection = Object.keys(state.articleInstanceSelections).length > 0;
            isAnchorInsertedThisEvent =
                hasInsertedSelection &&
                insertedSettingType === SettingModelSettingType.Anchors &&
                isAnchorArticle(state.articles[selectedArticleId ?? '']?.articleNumber ?? null);
            if (hasInsertedSelection && isSolverGuidedSettingType(insertedSettingType)) {
                isManualInsertionPendingSnap = true;
            }
            state.setIsDragNewArticleStartRef(false);
            state.setIsDragNewArticleActiveRef(true);
            state.setIsDragActiveRef(true);
            isDragStart = true;
        }

        const currentSelectionIds = Object.keys(state.articleInstanceSelections);
        const selectedAnchorInstanceId = snapAnchorInstanceId ?? currentSelectionIds[0];
        const selectedAnchorArticleId = selectedAnchorInstanceId
            ? getArticleId(selectedAnchorInstanceId, state)
            : undefined;
        if (
            state.manualPlacementData.isLoading &&
            getActiveManualSettingType(state, selectedAnchorArticleId) === SettingModelSettingType.Anchors
        ) {
            return;
        }

        if (!get().isDragActiveRef.current && !get().isDragNewArticleActiveRef.current) {
            return;
        }

        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const dragRefsMap = state.articleDragRefsMap.current;
        const workingPlane = state.workingPlane;

        // create instanceToArticleIdMap
        updateInstanceToArticleIdMap(selectedInstanceIds, state);
        const dragAnchorInstanceId = snapAnchorInstanceId ?? selectedInstanceIds[0];
        const activeManualArticleId =
            getConnectorArticleId(state, dragAnchorInstanceId) ?? getArticleId(dragAnchorInstanceId, state) ?? null;
        const manualInsertionPlane = getManualPlacementPlane(state, activeManualArticleId);
        const isCursorPlacement = state.isDragNewArticleActiveRef.current || state.isAutoDragActiveRef.current;
        const cursorPlacementPlane = isCursorPlacement ? getPreferredInsertionPlane(state, manualInsertionPlane) : null;

        // check whether any selected instance has registered drag refs
        const hasAnyRegisteredRefs = hasAnyRegisteredDragRefs(selectedInstanceIds, dragRefsMap);
        if (!hasAnyRegisteredRefs) {
            if (
                !isAnchorInsertedThisEvent &&
                !isManualInsertionPendingSnap &&
                event.ray.intersectPlane(cursorPlacementPlane ?? raycastPlane, planeIntersectPoint)
            ) {
                const normal =
                    isCursorPlacement && manualInsertionPlane?.normal
                        ? manualInsertionPlane.normal
                        : isCursorPlacement && workingPlane?.normal
                          ? workingPlane.normal
                          : !isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0
                            ? sharedAlignmentNormal
                            : null;
                alignSelectionToCursorPoint(state, planeIntersectPoint, snapAnchorInstanceId, normal);
            }

            return;
        }

        const mainCameraControlsRef = state.mainCameraControlsRef;

        const dragAnchorId = snapAnchorInstanceId ?? selectedInstanceIds[0];
        const draggedArticleId =
            getConnectorArticleId(state, dragAnchorId) ?? getArticleId(dragAnchorId, state) ?? null;
        const isManualDragActive = state.isDragNewArticleActiveRef.current || state.isDragActiveRef.current;
        const draggedSettingType = isManualDragActive ? getActiveManualSettingType(state, draggedArticleId) : undefined;
        const isConnectorDrag = draggedSettingType === SettingModelSettingType.Connectors;
        const isAnchorDrag =
            draggedSettingType === SettingModelSettingType.Anchors && state.manualPlacementData.candidates.length > 0;
        const isSolverGuidedManualDrag = isConnectorDrag || isAnchorDrag;

        const isSolverGuidedCornerDrag =
            draggedArticleId !== null &&
            state.manualPlacementData.articleId === draggedArticleId &&
            state.manualPlacementData.settingType === SettingModelSettingType.Corners &&
            state.manualPlacementData.candidates.length > 0 &&
            (state.isDragNewArticleActiveRef.current || state.isAutoDragActiveRef.current);

        // calculate drag offsets of the selected article instances
        if (mainCameraControlsRef.current && isDragStart) {
            const isDragNewArticle = state.isDragNewArticleActiveRef.current;
            isTopBottomViewDrag = isTopBottomCameraView(raycastPlane.normal);
            isMixedAlignmentDrag = isMixedAlignmentSelection(selectedInstanceIds, dragRefsMap);

            const useZAxisOnlyDrag =
                isSolverGuidedCornerDrag ||
                (!isDragNewArticle && !isSolverGuidedManualDrag && shouldUseZAxisOnlyDrag(selectedInstanceIds, state));

            state.setIsZAxisDragActiveRef(useZAxisOnlyDrag);
            const preferredInsertionPlane = cursorPlacementPlane;

            // Update groupMemberIds once at drag start
            groupMemberIds.clear();
            if (!isDragNewArticle) {
                for (const id of selectedInstanceIds) {
                    if (state.instanceToGroupMap[id]) {
                        groupMemberIds.add(id);
                    }
                }
            }
            isGroupDragActive = groupMemberIds.size > 0;

            if (!isMixedAlignmentDrag) {
                // Store the shared wall normal for delta projection during drag.
                const { anchorInstanceData: firstInstance } = getAnchorDragContext(selectedInstanceIds[0], dragRefsMap);
                if (firstInstance) {
                    getAnchorAlignment(firstInstance, sharedAlignmentNormal);
                }
            }

            updateInstanceToPoiIndexMap(selectedInstanceIds, state);

            // Update the active POI lookups
            activePoiGuids.clear();
            for (const instanceId of selectedInstanceIds) {
                const poiIndex = instanceToPoiIndexMap.get(instanceId);
                if (poiIndex !== undefined) {
                    const poiGuid = state.pois[poiIndex]?.poi?.poiGuid;
                    if (poiGuid) {
                        activePoiGuids.add(poiGuid);
                    }
                }
            }

            if (
                isTopBottomViewDrag &&
                isDragNewArticle &&
                state.selectedArticleId &&
                selectedInstanceIds.length === 1
            ) {
                placeInstanceOnWall(selectedInstanceIds[0], state.selectedArticleId, state, dragRefsMap);
            }

            const hasInitialPointIntersection = event.ray.intersectPlane(
                preferredInsertionPlane ?? raycastPlane,
                initialPoint
            );

            if (!hasInitialPointIntersection) {
                return;
            }

            const usedragStartPoint = hasDragStartPoint && !isDragNewArticle && !state.isAutoDragActiveRef.current;
            const dragPoint = usedragStartPoint ? dragStartPoint : initialPoint;
            hasDragStartPoint = false;

            targetPosition.copy(dragPoint);
            hasPreviousDragPoint = true;

            if (!snapAnchorInstanceId || !selectedInstanceIds.includes(snapAnchorInstanceId)) {
                snapAnchorInstanceId = selectedInstanceIds[0];
            }

            // Keep a stable drag reference (grab point / insertionpoint) relative to the snap anchor.
            hasDragStartReferenceOffset = false;
            if (snapAnchorInstanceId) {
                const { anchorArticleId, anchorInstanceData } = getAnchorDragContext(snapAnchorInstanceId, dragRefsMap);
                if (
                    anchorInstanceData &&
                    getAnchorPosition(
                        anchorInstanceData,
                        anchorPosition,
                        getConnectorMainPartName(anchorArticleId, dragRefsMap)
                    )
                ) {
                    if (!dragAnchorPositionRef.current) {
                        dragAnchorPositionRef.current = anchorPosition.clone();
                    } else {
                        dragAnchorPositionRef.current.copy(anchorPosition);
                    }

                    if (!isCursorPlacement) {
                        raycastPlane.setFromNormalAndCoplanarPoint(raycastPlane.normal, anchorPosition);
                    }

                    if (isDragNewArticle && anchorArticleId) {
                        const insertionPoint = state.computeArticleInsertionPoint(anchorArticleId, anchorInstanceData);
                        if (insertionPoint) {
                            dragStartReferenceOffset.copy(dragPoint).sub(insertionPoint);
                            hasDragStartReferenceOffset = true;
                        }
                    }

                    if (!hasDragStartReferenceOffset) {
                        dragStartReferenceOffset.copy(dragPoint).sub(anchorPosition);
                        hasDragStartReferenceOffset = true;
                    }
                }
            }

            // Only use per-instance drag offsets for manual article dragging - otherwise move together as a rigid group
            if (isDragNewArticle) {
                perInstanceDragOffsets.clear();
                const instancePos = new Vector3();
                for (const instanceId of selectedInstanceIds) {
                    if (instanceId === snapAnchorInstanceId) {
                        continue;
                    }
                    const articleId = instanceToArticleIdMap.get(instanceId);
                    const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
                    const instanceData = dragRef?.articleRef.current?.articleInstances[instanceId];
                    if (
                        instanceData &&
                        getAnchorPosition(instanceData, instancePos, getConnectorMainPartName(articleId, dragRefsMap))
                    ) {
                        perInstanceDragOffsets.set(instanceId, dragPoint.clone().sub(instancePos));
                    }
                }
            }

            perInstancePlanes.clear();
            perInstancePrevIntersections.clear();
            initializePerInstancePlanesAndIntersections(
                selectedInstanceIds,
                dragRefsMap,
                event.ray,
                perInstancePlanes,
                perInstancePrevIntersections
            );

            lastSnapTime = isCursorPlacement ? Number.NEGATIVE_INFINITY : performance.now();

            const activeDraggingBox = selectedInstanceIds.length > DRAGGING_BOX_THRESHOLD;

            // hide all non-anchor instances at drag start
            if (activeDraggingBox) {
                instancesByArticleId.clear();
                const grouped = groupSelectedInstancesByArticle(
                    selectedInstanceIds.filter((instanceId) => instanceId !== snapAnchorInstanceId)
                );
                for (const [articleId, ids] of grouped) {
                    instancesByArticleId.set(articleId, ids);
                }
                for (const [articleId, ids] of instancesByArticleId) {
                    const dragRef = dragRefsMap.get(articleId);
                    if (dragRef) {
                        state.updateInstanceMatrices(true, dragRef.articleRef, false, ids, false);
                    }
                }
            }

            // remove colliders of the selected instances except the snap anchor
            const collidersToRemove =
                activeDraggingBox && snapAnchorInstanceId
                    ? selectedInstanceIds.filter((id) => id !== snapAnchorInstanceId)
                    : selectedInstanceIds;

            state.removeCollidersByRelations(collidersToRemove);
            // The KD-tree rebuild is deferred, so stale colliders of dragged instances must be excluded explicitly.
            syncDraggedSnapRelationIds(selectedInstanceIds);
            handleDragSettings(isDragStart, state.mainWebGLRendererRef);
            isDragStart = false;

            if (
                (preferredInsertionPlane && event.ray.intersectPlane(preferredInsertionPlane, planeIntersectPoint)) ||
                event.ray.intersectPlane(raycastPlane, planeIntersectPoint)
            ) {
                targetPosition.copy(planeIntersectPoint);
            }
        }

        // handle article instance dragging
        if (mainCameraControlsRef.current && !isDragStart) {
            const preferredDragPlane = cursorPlacementPlane;

            const hasDragPlaneIntersection = preferredDragPlane
                ? event.ray.intersectPlane(preferredDragPlane, planeIntersectPoint)
                : event.ray.intersectPlane(raycastPlane, planeIntersectPoint);

            if (!hasDragPlaneIntersection) {
                return;
            }

            if (!hasPreviousDragPoint) {
                targetPosition.copy(planeIntersectPoint);
                hasPreviousDragPoint = true;
            }

            // Reset drag state for per-frame instance grouping and anchor delta
            instancesByArticleId.clear();
            anchorMovedDelta.set(0, 0, 0);

            // When dragging-box mode is active then move the entire selection as a rigid group
            const useDraggingBoxMovement = selectedInstanceIds.length > DRAGGING_BOX_THRESHOLD;
            dragDelta.subVectors(planeIntersectPoint, targetPosition);

            if (isAnchorInsertedThisEvent || isManualInsertionPendingSnap) {
                dragDelta.set(0, 0, 0);
            }

            if (state.isZAxisDragActiveRef.current) {
                dragDelta.x = 0;
                dragDelta.y = 0;
            } else if (!isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0) {
                projectDeltaToWorkingPlane(dragDelta, sharedAlignmentNormal);
            }

            // Lock Z axis when top/bottom view drag is active
            if (isTopBottomViewDrag) {
                dragDelta.z = 0;
            }

            const loopInstanceIds =
                (useDraggingBoxMovement || isGroupDragActive) && snapAnchorInstanceId
                    ? [snapAnchorInstanceId, ...selectedInstanceIds.filter((id) => id !== snapAnchorInstanceId)]
                    : selectedInstanceIds;

            if (isCursorPlacement && !isAnchorInsertedThisEvent && !isManualInsertionPendingSnap) {
                const normal = manualInsertionPlane?.normal ?? workingPlane?.normal ?? null;
                if (alignSelectionToCursorPoint(state, planeIntersectPoint, snapAnchorInstanceId, normal)) {
                    anchorMovedDelta.copy(dragDelta);
                }
            }

            for (const instanceId of loopInstanceIds) {
                const articleId = instanceToArticleIdMap.get(instanceId);
                const articleDragRef = articleId ? dragRefsMap.get(articleId) : undefined;
                if (!articleId || !articleDragRef) {
                    continue;
                }
                const instanceData = articleDragRef.articleRef.current?.articleInstances[instanceId];
                if (!instanceData) {
                    continue;
                }

                // Cursor placement is absolute; only existing-object drags accumulate ray deltas.
                if (isCursorPlacement) {
                    appendGroupedInstance(instancesByArticleId, articleId, instanceId);
                    continue;
                }

                if (state.isZAxisDragActiveRef.current) {
                    if (dragDelta.x !== 0 || dragDelta.y !== 0 || dragDelta.z !== 0) {
                        applyMoveToInstance(instanceId, instanceData, dragDelta);
                    }
                } else if (
                    (useDraggingBoxMovement || (isGroupDragActive && groupMemberIds.has(instanceId))) &&
                    instanceId !== snapAnchorInstanceId
                ) {
                    // Rigid group: for dragging-box mode and for group dragging
                    if (anchorMovedDelta.x !== 0 || anchorMovedDelta.y !== 0 || anchorMovedDelta.z !== 0) {
                        applyMoveToInstance(instanceId, instanceData, anchorMovedDelta);
                    }
                } else if (
                    computePerInstanceWallDelta(instanceId, event.ray, isTopBottomViewDrag, dragDelta, perInstanceDelta)
                ) {
                    applyMoveToInstance(instanceId, instanceData, perInstanceDelta);
                    if (instanceId === snapAnchorInstanceId) {
                        anchorMovedDelta.copy(perInstanceDelta);
                    }
                }

                appendGroupedInstance(instancesByArticleId, articleId, instanceId);
            }

            {
                const anchorId = snapAnchorInstanceId ?? selectedInstanceIds[0];
                const { anchorInstanceData: liveAnchorData } = getAnchorDragContext(anchorId, dragRefsMap);
                if (
                    liveAnchorData &&
                    getAnchorPosition(
                        liveAnchorData,
                        anchorPosition,
                        getConnectorMainPartName(
                            anchorId ? instanceToArticleIdMap.get(anchorId) : undefined,
                            dragRefsMap
                        )
                    )
                ) {
                    if (!dragAnchorPositionRef.current) {
                        dragAnchorPositionRef.current = anchorPosition.clone();
                    } else {
                        dragAnchorPositionRef.current.copy(anchorPosition);
                    }

                    if (!isCursorPlacement) {
                        raycastPlane.setFromNormalAndCoplanarPoint(raycastPlane.normal, anchorPosition);
                    }
                }
            }

            targetPosition.copy(planeIntersectPoint);

            // Translate dragging box by the anchor's wall-plane
            if (draggingBoxStatus.isComputed && !isSolverGuidedManualDrag) {
                const boxDelta = anchorMovedDelta.lengthSq() > 0 ? anchorMovedDelta : dragDelta;
                if (boxDelta.x !== 0 || boxDelta.y !== 0 || boxDelta.z !== 0) {
                    translateDraggingBox(state.draggingBoxRef.current, boxDelta);
                }
            }

            const anchorId = snapAnchorInstanceId ?? selectedInstanceIds[0];
            const useDraggingBoxSnap = useDraggingBoxMovement;
            let snapPoints: Vector3[] | null = null;
            let isCornerSnap = false;
            let isManualSnap = false;
            const snapCamera = state.mainCameraControlsRef.current?.camera;
            const viewportHeight = state.mainWebGLRendererRef.current?.domElement.clientHeight ?? 0;
            const captureDistance = getSnapCaptureDistance(snapCamera, viewportHeight, planeIntersectPoint);
            if (event.altKey) {
                cachedSnapPoints = null;
                connectorSnapCandidateIndex = null;
                state.snapPointPreviewRef.current = null;
                state.setAdditionalSnapPointFeedback([]);
            }

            // pre-compute anchor instance data for snap cache delta-correction
            const { anchorInstanceData: snapAnchorInstance } = getAnchorDragContext(anchorId, dragRefsMap);

            // throttle snapping computation to avoid blocking the main thread
            const now = performance.now();
            const shouldComputeSnap =
                !event.altKey && (isSolverGuidedManualDrag || now - lastSnapTime >= SNAP_THROTTLE_MS);
            if (shouldComputeSnap) {
                syncDraggedSnapRelationIds(selectedInstanceIds);
                state.snapPointPreviewRef.current = null;
            }
            if (isSolverGuidedManualDrag && shouldComputeSnap) {
                state.setAdditionalSnapPointFeedback([]);
            }

            if (isSolverGuidedManualDrag && shouldComputeSnap) {
                const zoom = state.mainCameraControlsRef.current?.camera.zoom ?? 1;
                const connectorCaptureDistance = isManualInsertionPendingSnap
                    ? Number.POSITIVE_INFINITY
                    : getSnapCaptureDistance(
                          snapCamera,
                          viewportHeight,
                          planeIntersectPoint,
                          getConnectorSnapCaptureDistance(zoom),
                          CONNECTOR_SNAP_CAPTURE_RADIUS_PX
                      );
                const manualPoiIndex = state.manualPlacementData.poiIndex;
                const isCornerConnector =
                    manualPoiIndex !== undefined && state.pois[manualPoiIndex]?.filterType === POIFilterType.Corner;
                const useAnchorDragDelta =
                    isAnchorDrag &&
                    !isCursorPlacement &&
                    snapAnchorInstance &&
                    getAnchorPosition(
                        snapAnchorInstance,
                        currentSnapAnchorPosition,
                        getConnectorMainPartName(instanceToArticleIdMap.get(anchorId), dragRefsMap)
                    );
                const adjustedSnapSource = getConnectorSnapSource(
                    useAnchorDragDelta ? currentSnapAnchorPosition : planeIntersectPoint,
                    manualInsertionPlane,
                    state.workingPlane
                );

                const snapPoints = state.manualPlacementData.snapPoints;
                const snapRanges = state.manualPlacementData.snapRanges;
                const hasProjectedSnapGeometry = snapPoints.length > 0 || snapRanges.length > 0;
                let resolvedManualTarget = hasProjectedSnapGeometry
                    ? findNearestConnectorSnapTarget(
                          adjustedSnapSource,
                          event.ray,
                          snapPoints,
                          snapRanges,
                          connectorCaptureDistance,
                          isCornerConnector,
                          connectorSnapCandidateIndex
                      )
                    : null;

                const connectorMainPartName = getConnectorMainPartName(
                    instanceToArticleIdMap.get(anchorId),
                    dragRefsMap
                );
                if (resolvedManualTarget && connectorMainPartName) {
                    resolvedManualTarget = normalizeAdjustableConnectorSnapTarget(
                        resolvedManualTarget,
                        connectorMainPartName
                    );
                }

                if (resolvedManualTarget) {
                    isManualSnap = true;
                    connectorSnapCandidateIndex = resolvedManualTarget.index;
                    // Remember the free (unsnapped) anchor position of existing-object drags, so the snap offset can be
                    // reverted on the next pointer move and the object re-syncs with the cursor once the snap releases.
                    const hasFreeAnchorPosition =
                        !isCursorPlacement &&
                        !!snapAnchorInstance &&
                        (useAnchorDragDelta ||
                            getAnchorPosition(snapAnchorInstance, currentSnapAnchorPosition, connectorMainPartName));
                    if (
                        state.isDragNewArticleActiveRef.current &&
                        isFramiWedgeClamp(state.articles[state.selectedArticleId ?? '']?.articleNumber ?? null)
                    ) {
                        manualSnapSourceInstanceId = resolvedManualTarget.sourceInstanceId;
                    }
                    isManualInsertionPendingSnap = false;

                    const wallPoiIndex = state.pois.findIndex(
                        (poiData) => poiData.poi.poiGuid === resolvedManualTarget.wallPoiId
                    );
                    const wallSnapPlane = createConnectorSnapPlane(resolvedManualTarget.placement);
                    if (
                        wallPoiIndex >= 0 &&
                        resolvedManualTarget.wallPoiId !== connectorSnapWallPoiId &&
                        updateWallPlacementTarget(state, selectedInstanceIds, wallPoiIndex, snapAnchorInstanceId, {
                            placementPlane: wallSnapPlane ?? undefined,
                            persistPoiReference: false
                        })
                    ) {
                        connectorSnapWallPoiId = resolvedManualTarget.wallPoiId;
                    }

                    const snapTarget = new Vector3(
                        resolvedManualTarget.placement.position.x,
                        resolvedManualTarget.placement.position.y,
                        resolvedManualTarget.placement.position.z
                    );

                    const manualConstraintNormal = new Vector3(
                        resolvedManualTarget.placement.alignment.x,
                        resolvedManualTarget.placement.alignment.y,
                        resolvedManualTarget.placement.alignment.z
                    ).normalize();
                    const snapPlane =
                        wallSnapPlane ?? new Plane().setFromNormalAndCoplanarPoint(manualConstraintNormal, snapTarget);
                    state.manualPlacementData.plane?.copy(snapPlane);
                    if (!isTopBottomViewDrag) {
                        raycastPlane.copy(snapPlane);
                    }

                    const didSnap = alignSelectionToCursorPoint(
                        state,
                        snapTarget,
                        snapAnchorInstanceId,
                        manualConstraintNormal,
                        resolvedManualTarget.placement.name
                    );

                    let didApplyPairedSnap = false;
                    if (selectedInstanceIds.length === 1) {
                        const instanceId = selectedInstanceIds[0];
                        const articleId = instanceToArticleIdMap.get(instanceId);
                        const instanceData = articleId
                            ? dragRefsMap.get(articleId)?.articleRef.current?.articleInstances[instanceId]
                            : undefined;
                        const mainPartName = getConnectorMainPartName(articleId, dragRefsMap);
                        if (instanceData && mainPartName) {
                            didApplyPairedSnap = alignAdjustableConnectorPartsToSnapTarget(
                                instanceData,
                                mainPartName,
                                resolvedManualTarget
                            );
                            if (didApplyPairedSnap && articleId) {
                                refreshInstanceColliderCache(state, instanceId, instanceData, articleId, dragRefsMap);
                                state.setAdditionalSnapPointFeedback(
                                    getAdditionalConnectorSnapFeedback(instanceData, resolvedManualTarget, true)
                                );
                            }
                        }
                    }

                    if (hasFreeAnchorPosition) {
                        if (getAnchorPosition(snapAnchorInstance, anchorPosition, connectorMainPartName)) {
                            snapOffset.subVectors(anchorPosition, currentSnapAnchorPosition);
                            // Like panel snapping, account for the snap translation in the next pointer delta.
                            event.ray.intersectPlane(raycastPlane, targetPosition);
                            targetPosition.add(snapOffset);
                            for (const [instanceId, prevPt] of perInstancePrevIntersections) {
                                const plane = perInstancePlanes.get(instanceId);
                                if (plane && event.ray.intersectPlane(plane, prevPt)) {
                                    snapProjDelta.copy(snapOffset);
                                    projectDeltaToWorkingPlane(snapProjDelta, plane.normal);
                                    prevPt.add(snapProjDelta);
                                }
                            }
                        }
                    } else if (didSnap || didApplyPairedSnap) {
                        targetPosition.copy(snapTarget);
                    }

                    if (!didApplyPairedSnap) {
                        applyRotationToSelectedInstances(
                            selectedInstanceIds,
                            dragRefsMap,
                            resolvedManualTarget.placement.rotation
                        );
                    }
                    if (isCornerConnector && !didApplyPairedSnap) {
                        applyAlignmentToSelectedInstances(selectedInstanceIds, dragRefsMap, manualConstraintNormal);
                    }
                    if (resolvedManualTarget.poiGuids.length > 0) {
                        for (const instanceId of selectedInstanceIds) {
                            const articleId = instanceToArticleIdMap.get(instanceId);
                            const instanceData = articleId
                                ? dragRefsMap.get(articleId)?.articleRef.current?.articleInstances[instanceId]
                                : undefined;
                            if (instanceData) {
                                instanceData.poiGuids = [...resolvedManualTarget.poiGuids];
                            }
                        }
                    }

                    state.snapPointMarkerRef.current?.position.copy(snapTarget);
                } else {
                    connectorSnapCandidateIndex = null;
                    const previewTarget = findNearestConnectorSnapTarget(
                        adjustedSnapSource,
                        event.ray,
                        snapPoints,
                        snapRanges,
                        connectorCaptureDistance * SNAP_PREVIEW_DISTANCE_FACTOR,
                        isCornerConnector
                    );
                    if (previewTarget) {
                        const normalizedTarget = connectorMainPartName
                            ? normalizeAdjustableConnectorSnapTarget(previewTarget, connectorMainPartName)
                            : previewTarget;
                        const { position } = normalizedTarget.placement;
                        state.snapPointPreviewRef.current = [
                            new Vector3(position.x, position.y, position.z),
                            adjustedSnapSource.clone()
                        ];
                        if (snapAnchorInstance) {
                            state.setAdditionalSnapPointFeedback(
                                getAdditionalConnectorSnapFeedback(snapAnchorInstance, normalizedTarget, false)
                            );
                        }
                    }
                }
            } else if (shouldComputeSnap) {
                lastSnapTime = now;

                // A capture hit implies a hit within the wider preview radius, so one preview pass
                // lets the capture passes skip every instance with nothing nearby.
                previewSnapByInstance.clear();
                if (!useDraggingBoxSnap) {
                    for (const candidateId of selectedInstanceIds) {
                        const preview = state.calculateSnappingPosition(
                            candidateId,
                            true,
                            undefined,
                            undefined,
                            SNAP_PREVIEW_DISTANCE_FACTOR
                        );
                        if (preview) {
                            previewSnapByInstance.set(candidateId, preview);
                        }
                    }
                }
                const canSnap = (instanceId: string) => useDraggingBoxSnap || previewSnapByInstance.has(instanceId);

                // Handle corner snapping
                if (anchorId && canSnap(anchorId)) {
                    snapPoints = state.calculateSnappingPosition(anchorId, false);
                }

                if (!snapPoints && !useDraggingBoxSnap) {
                    for (const candidateId of selectedInstanceIds) {
                        if (candidateId === anchorId || !canSnap(candidateId)) {
                            continue;
                        }
                        snapPoints = state.calculateSnappingPosition(candidateId, false);
                        if (snapPoints) {
                            break;
                        }
                    }
                }

                if (snapPoints) {
                    isCornerSnap = true;
                }

                // Handle dragging-box
                if (!snapPoints && useDraggingBoxSnap) {
                    const draggingBox = state.draggingBoxRef.current;

                    if (!draggingBox.boundingBox.isEmpty()) {
                        const { min, max } = draggingBox.boundingBox;
                        const localToWorld = draggingBox.localToWorld;

                        let closestCornerSnap: { corner: Vector3; target: Vector3; distance: number } | null = null;
                        let closestEdgeSnap: { corner: Vector3; target: Vector3; distance: number } | null = null;

                        for (let xi = 0; xi <= 1; xi++) {
                            for (let yi = 0; yi <= 1; yi++) {
                                for (let zi = 0; zi <= 1; zi++) {
                                    worldCorner
                                        .set(
                                            xi === 0 ? min.x : max.x,
                                            yi === 0 ? min.y : max.y,
                                            zi === 0 ? min.z : max.z
                                        )
                                        .applyMatrix4(localToWorld);

                                    if (workingPlane) {
                                        workingPlane.projectPoint(worldCorner, projectedCorner);
                                    } else {
                                        projectedCorner.copy(worldCorner);
                                    }

                                    // One bounded lookup at preview range serves both capture and preview.
                                    const previewDistance = captureDistance * SNAP_PREVIEW_DISTANCE_FACTOR;
                                    const nearestCollider = state.findNearest(projectedCorner, {
                                        excludeRelationIds: draggedSnapRelationIds,
                                        maxDistance: previewDistance,
                                        maxResultsCount: 1,
                                        neighborExpansionFactor: 2
                                    });
                                    const colliderDistance = nearestCollider
                                        ? projectedCorner.distanceTo(nearestCollider.position)
                                        : Number.POSITIVE_INFINITY;

                                    if (nearestCollider && colliderDistance <= captureDistance) {
                                        if (!closestCornerSnap || colliderDistance < closestCornerSnap.distance) {
                                            closestCornerSnap = {
                                                corner: projectedCorner.clone(),
                                                target: nearestCollider.position.clone(),
                                                distance: colliderDistance
                                            };
                                        }
                                    } else {
                                        const edgePoint = state.findNearestPointOnBuildingEdges(
                                            projectedCorner,
                                            captureDistance,
                                            activePoiGuids
                                        );
                                        if (edgePoint) {
                                            const distance = projectedCorner.distanceTo(edgePoint);
                                            if (!closestEdgeSnap || distance < closestEdgeSnap.distance) {
                                                closestEdgeSnap = {
                                                    corner: projectedCorner.clone(),
                                                    target: edgePoint,
                                                    distance
                                                };
                                            }
                                        }
                                        const previewTarget =
                                            nearestCollider?.position ??
                                            state.findNearestPointOnBuildingEdges(
                                                projectedCorner,
                                                previewDistance,
                                                activePoiGuids
                                            );
                                        const previousPreview = state.snapPointPreviewRef.current;
                                        if (
                                            previewTarget &&
                                            (!previousPreview ||
                                                projectedCorner.distanceToSquared(previewTarget) <
                                                    previousPreview[0].distanceToSquared(previousPreview[1]))
                                        ) {
                                            state.snapPointPreviewRef.current = [
                                                previewTarget.clone(),
                                                projectedCorner.clone()
                                            ];
                                        }
                                    }
                                }
                            }
                        }

                        if (closestCornerSnap) {
                            snapPoints = [closestCornerSnap.target, closestCornerSnap.corner];
                            isCornerSnap = true;
                        } else if (closestEdgeSnap) {
                            snapPoints = [closestEdgeSnap.target, closestEdgeSnap.corner];
                        }
                    }
                }

                // Handle edge snapping when no corner snapping points are found
                if (!snapPoints) {
                    if (anchorId && canSnap(anchorId)) {
                        snapPoints = state.calculateSnappingPosition(anchorId, true);
                    }
                    if (!snapPoints && !useDraggingBoxSnap) {
                        for (const candidateId of selectedInstanceIds) {
                            if (candidateId === anchorId || !canSnap(candidateId)) {
                                continue;
                            }
                            snapPoints = state.calculateSnappingPosition(candidateId, true);
                            if (snapPoints) {
                                break;
                            }
                        }
                    }
                }

                cachedSnapPoints = snapPoints;
                if (!snapPoints && !useDraggingBoxSnap) {
                    for (const candidateId of selectedInstanceIds) {
                        const preview = previewSnapByInstance.get(candidateId);
                        const previousPreview = state.snapPointPreviewRef.current;
                        if (
                            preview &&
                            (!previousPreview ||
                                preview[0].distanceToSquared(preview[1]) <
                                    previousPreview[0].distanceToSquared(previousPreview[1]))
                        ) {
                            state.snapPointPreviewRef.current = [preview[0], preview[1]];
                        }
                    }
                }
                isCornerSnapActive = isCornerSnap;
                cachedIntersect.copy(planeIntersectPoint);
                if (
                    snapAnchorInstance &&
                    getAnchorPosition(
                        snapAnchorInstance,
                        cachedSnapAnchorPosition,
                        getConnectorMainPartName(
                            anchorId ? instanceToArticleIdMap.get(anchorId) : undefined,
                            dragRefsMap
                        )
                    )
                ) {
                    if (hasDragStartReferenceOffset) {
                        cachedSnapAnchorPosition.add(dragStartReferenceOffset);
                    }
                    hasCachedSnapAnchorPosition = true;
                } else {
                    hasCachedSnapAnchorPosition = false;
                }
            } else if (!event.altKey && !isSolverGuidedManualDrag) {
                // reuse last computed snapping point, adjusted for how much the anchor moved since
                if (cachedSnapPoints) {
                    if (
                        snapAnchorInstance &&
                        hasCachedSnapAnchorPosition &&
                        getAnchorPosition(
                            snapAnchorInstance,
                            currentSnapAnchorPosition,
                            getConnectorMainPartName(
                                anchorId ? instanceToArticleIdMap.get(anchorId) : undefined,
                                dragRefsMap
                            )
                        )
                    ) {
                        if (hasDragStartReferenceOffset) {
                            currentSnapAnchorPosition.add(dragStartReferenceOffset);
                        }
                        adjustedSnapSource
                            .copy(cachedSnapPoints[1])
                            .add(currentSnapAnchorPosition)
                            .sub(cachedSnapAnchorPosition);
                    } else {
                        adjustedSnapSource.copy(cachedSnapPoints[1]);
                    }

                    snapPoints = cachedSnapPoints;
                    isCornerSnap = isCornerSnapActive;
                }
            }

            if (snapPoints) {
                state.snapPointMarkerRef.current?.position.copy(snapPoints[0]);
                if (isSolverGuidedManualDrag) {
                    const manualConstraintNormal = manualInsertionPlane?.normal ?? workingPlane?.normal ?? null;
                    if (
                        alignSelectionToCursorPoint(state, snapPoints[0], snapAnchorInstanceId, manualConstraintNormal)
                    ) {
                        targetPosition.copy(snapPoints[0]);
                    }
                } else {
                    if (!shouldComputeSnap && hasCachedSnapAnchorPosition) {
                        snapOffset.subVectors(snapPoints[0], adjustedSnapSource);
                    } else {
                        snapOffset.subVectors(snapPoints[0], snapPoints[1]);
                    }

                    const normal =
                        !isMixedAlignmentDrag && sharedAlignmentNormal.lengthSq() > 0 ? sharedAlignmentNormal : null;
                    if (normal) {
                        snapOffset.addScaledVector(normal, -snapOffset.dot(normal));
                    }

                    if (state.isZAxisDragActiveRef.current) {
                        snapOffset.x = 0;
                        snapOffset.y = 0;
                    }

                    if (isTopBottomViewDrag) {
                        snapOffset.z = 0;
                    }

                    applySnapOffsetToInstances(
                        selectedInstanceIds,
                        dragRefsMap,
                        snapOffset,
                        perInstancePlanes,
                        useDraggingBoxMovement ? true : isGroupDragActive ? groupMemberIds : false
                    );

                    // Snap translates instances outside of cursor delta, so keep drag baseline in sync.
                    targetPosition.add(snapOffset);

                    if (snapOffset.lengthSq() > 0) {
                        for (const [snapInstanceId, prevPt] of perInstancePrevIntersections) {
                            const plane = perInstancePlanes.get(snapInstanceId);
                            if (plane) {
                                snapProjDelta.copy(snapOffset);
                                projectDeltaToWorkingPlane(snapProjDelta, plane.normal);
                                prevPt.add(snapProjDelta);
                            }
                        }
                    }
                }

                // Also translate dragging box by the snap offset
                if (draggingBoxStatus.isComputed && !isSolverGuidedManualDrag) {
                    translateDraggingBox(state.draggingBoxRef.current, snapOffset);
                }

                // Handle edges snapping marker
                if (!isCornerSnap && snapPoints.length >= 2) {
                    const snappedEdge = state.findNearestBuildingEdgeSegment(snapPoints[0], 0.02, activePoiGuids);
                    if (snappedEdge !== lastSnappedEdge) {
                        lastSnappedEdge = snappedEdge;
                        if (snappedEdge) {
                            get().setSnapEdgeMarker({
                                start: [snappedEdge.start.x, snappedEdge.start.y, snappedEdge.start.z],
                                end: [snappedEdge.end.x, snappedEdge.end.y, snappedEdge.end.z]
                            });
                        } else {
                            get().setSnapEdgeMarker(null);
                        }
                    }
                } else if (lastSnappedEdge !== null) {
                    lastSnappedEdge = null;
                    get().setSnapEdgeMarker(null);
                }
            } else {
                if (lastSnappedEdge !== null) {
                    lastSnappedEdge = null;
                    get().setSnapEdgeMarker(null);
                }
            }

            handleMarkerVisibility(state.snapPointMarkerRef, snapPoints !== null || isManualSnap);
            if (snapPoints || isManualSnap) {
                state.snapPointPreviewRef.current = null;
            }
            if (!event.altKey && !isSolverGuidedManualDrag && shouldComputeSnap) {
                const feedback: SnapPointFeedback[] = [];
                const primaryTarget = snapPoints?.[0] ?? state.snapPointPreviewRef.current?.[0];
                snapFeedbackTargetsByCell.clear();
                for (const instanceId of selectedInstanceIds) {
                    for (const source of dragColliderCache.get(instanceId) ?? []) {
                        const previewDistance =
                            getSnapCaptureDistance(snapCamera, viewportHeight, source) * SNAP_PREVIEW_DISTANCE_FACTOR;
                        const destination =
                            state.findNearest(source, {
                                excludeRelationIds: draggedSnapRelationIds,
                                maxDistance: previewDistance,
                                maxResultsCount: 1
                            })?.position ??
                            state.findNearestPointOnBuildingEdges(source, previewDistance, activePoiGuids);
                        if (
                            !destination ||
                            (primaryTarget && destination.distanceToSquared(primaryTarget) <= EPSILON * EPSILON)
                        ) {
                            continue;
                        }
                        const feedbackTarget = destination.clone();
                        if (isDuplicateSnapFeedbackTarget(feedbackTarget)) {
                            continue;
                        }
                        addSnapFeedbackTarget(feedbackTarget);
                        feedback.push({
                            target: feedbackTarget,
                            source:
                                source.distanceToSquared(destination) <= EPSILON * EPSILON ? undefined : source.clone()
                        });
                    }
                }
                state.setAdditionalSnapPointFeedback(feedback);
            }

            const useDraggingBox = useDraggingBoxMovement;
            const draggingBoxRef = state.draggingBoxRef;

            if (useDraggingBox) {
                const draggingBox = draggingBoxRef.current;

                const anchorInstanceId = snapAnchorInstanceId ?? selectedInstanceIds[0];
                const { anchorDragRef, anchorInstanceData } = getAnchorDragContext(anchorInstanceId, dragRefsMap);

                if (!draggingBoxStatus.isComputed && !draggingBoxStatus.isPending) {
                    scheduleDraggingBoxComputation(
                        selectedInstanceIds,
                        anchorInstanceId,
                        dragRefsMap,
                        draggingBox,
                        state
                    );
                }

                if (draggingBoxStatus.isComputed && anchorInstanceData) {
                    if (!draggingBox.isActive && !draggingBox.boundingBox.isEmpty()) {
                        draggingBox.isActive = true;
                    }
                }

                // only update the anchor article instance
                if (anchorDragRef && anchorInstanceId) {
                    state.updateInstanceMatrices(true, anchorDragRef.articleRef, false, [anchorInstanceId]);
                }
            } else {
                draggingBoxRef.current.isActive = false;

                // update instance matrices
                for (const [articleId, instancesOfType] of instancesByArticleId) {
                    const articleDragRef = dragRefsMap.get(articleId);
                    if (!articleDragRef) {
                        continue;
                    }
                    state.updateInstanceMatrices(true, articleDragRef.articleRef, false, instancesOfType);
                }
            }
        }
    },

    handleOnDragArticleEnd: (isCancelled = false) => {
        const state = get();
        clearHoveredHighlight(state);

        const wasNewArticlePlacement = state.isDragNewArticleActiveRef.current;
        const insertedArticleId = state.selectedArticleId;
        const snappedSupportInstanceId = manualSnapSourceInstanceId;
        const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
        const dragRefsMap = state.articleDragRefsMap.current;

        const scheduleEdgesOverlayUpdate = () => {
            requestAnimationFrame(() => {
                get().updateArticleEdgesOverlay();
            });
        };

        // Fallback: update z position when dragging got interrupted during top/bottom view insertion
        const isDragRefRegistered = dragRefsMap.get(state.selectedArticleId ?? '') !== undefined;
        const isTopBottomViewInsertion =
            isTopBottomCameraView(raycastPlane.normal) &&
            (state.isDragNewArticleActiveRef.current || state.isDragNewArticleStartRef.current);

        if (isTopBottomViewInsertion && selectedInstanceIds.length === 1 && isDragRefRegistered) {
            placeInstanceOnWall(selectedInstanceIds[0], state.selectedArticleId, state, dragRefsMap);
        }

        const isZPostionUpdatePending =
            isTopBottomViewInsertion && (!isDragRefRegistered || selectedInstanceIds.length === 0);
        state.setIsZPositionUpdatePending(isZPostionUpdatePending);

        // Reset drag states
        state.setIsDragNewArticleActiveRef(false);
        state.setIsDragActiveRef(false);
        state.setIsDragNewArticleStartRef(false);
        state.manualArticleInsertionButtonRef.current = null;
        state.setIsAutoDragActiveRef(false);
        state.resetHiddenPOIFilterTypes();
        state.setIsZAxisDragActiveRef(false);
        state.setSnapEdgeMarker(null);
        state.snapPointPreviewRef.current = null;
        state.setAdditionalSnapPointFeedback([]);
        draggedSnapRelationIds.clear();
        previewSnapByInstance.clear();

        isDragEnabled = false;
        isGroupDragActive = false;
        groupMemberIds.clear();
        isDragStart = true;

        if (state.arePivotControlsActive) {
            state.toggleRecomputeTransformationBox();
        }

        handleDragSettings(true, state.mainWebGLRendererRef);
        handleMarkerVisibility(state.snapPointMarkerRef, false);

        const draggingBoxRef = state.draggingBoxRef;
        const isDraggingBoxActive = draggingBoxRef.current.isActive;
        if (isDraggingBoxActive) {
            draggingBoxRef.current.isActive = false;
            draggingBoxRef.current.boundingBox.makeEmpty();
        }

        const groupedByArticle = groupSelectedInstancesByArticle(selectedInstanceIds);

        // fallback if instanceToArticleIdMap is empty
        if (selectedInstanceIds.length > 0 && groupedByArticle.size === 0) {
            const articles = state.articles;
            for (const instanceId of selectedInstanceIds) {
                for (const articleId of Object.keys(articles)) {
                    if (instanceId in articles[articleId].articleInstances) {
                        instanceToArticleIdMap.set(instanceId, articleId);

                        const group = groupedByArticle.get(articleId);
                        if (group) {
                            group.push(instanceId);
                        } else {
                            groupedByArticle.set(articleId, [instanceId]);
                        }
                        break;
                    }
                }
            }
        }

        const isCancellation = isCancelled;

        if (isCancellation) {
            const updateColliders = true;

            if (selectedInstanceIds.length > MATRIX_UPDATE_CHUNK_SIZE) {
                const groups = Array.from(groupedByArticle.entries());

                (async () => {
                    for (const [articleId, instanceIds] of groups) {
                        const dragRef = dragRefsMap.get(articleId);
                        if (!dragRef) {
                            continue;
                        }
                        for (let i = 0; i < instanceIds.length; i += MATRIX_UPDATE_CHUNK_SIZE) {
                            const chunk = instanceIds.slice(i, i + MATRIX_UPDATE_CHUNK_SIZE);
                            const isLastChunk = i + MATRIX_UPDATE_CHUNK_SIZE >= instanceIds.length;
                            state.updateInstanceMatrices(true, dragRef.articleRef, updateColliders, chunk);
                            if (!isLastChunk) {
                                await yieldToMain();
                            }
                        }
                    }
                })();
            } else {
                for (const [articleId, instanceIds] of groupedByArticle) {
                    const dragRef = dragRefsMap.get(articleId);
                    if (!dragRef) {
                        continue;
                    }

                    state.updateInstanceMatrices(true, dragRef.articleRef, updateColliders, instanceIds);
                }
            }
        }

        resetDragSettings();

        if (isCancellation) {
            scheduleEdgesOverlayUpdate();
            return;
        }

        if (selectedInstanceIds.length === 0) {
            scheduleEdgesOverlayUpdate();
            return;
        }

        const updatedArticleInstances: Array<{ articleId: string; instanceId: string; instance: ArticleInstance }> = [];

        for (const instanceId of selectedInstanceIds) {
            const articleId = instanceToArticleIdMap.get(instanceId) ?? state.selectedArticleId ?? undefined;
            if (!articleId) {
                continue;
            }

            const articleDragRef = dragRefsMap.get(articleId);
            const updatedInstances =
                articleDragRef?.articleRef.current?.articleInstances ?? state.articles[articleId]?.articleInstances;

            const instance = updatedInstances?.[instanceId];
            if (instance) {
                updatedArticleInstances.push({ articleId, instanceId, instance });
            }
        }

        if (updatedArticleInstances.length === 0) {
            scheduleEdgesOverlayUpdate();
            return;
        }

        state.updateArticleInstances(updatedArticleInstances, false);

        const latestState = get();
        const insertedClamp = updatedArticleInstances.find((entry) => entry.articleId === insertedArticleId);
        if (
            !isCancellation &&
            wasNewArticlePlacement &&
            snappedSupportInstanceId &&
            insertedClamp &&
            isFramiWedgeClamp(latestState.articles[insertedClamp.articleId]?.articleNumber ?? null)
        ) {
            joinSupportClampGroup(latestState, snappedSupportInstanceId, {
                articleId: insertedClamp.articleId,
                instanceId: insertedClamp.instanceId
            });
        }

        if (!isCancellation && latestState.autoPlaceSupportingConstructionClamps) {
            const movedSupports = updatedArticleInstances.filter(
                ({ articleId, instanceId }) =>
                    isSupportingConstruction(latestState.articles[articleId]?.articleNumber ?? null) &&
                    !!latestState.articles[articleId]?.articleInstances[instanceId]
            );
            for (const support of movedSupports) {
                void latestState.reconcileSupportingConstructionClamps?.(support.instanceId);
            }
        }
    },

    handleOnArticleSelection: (event, articleRef) => {
        const state = get();
        clearHoveredHighlight(state);

        state.setIsArticleDirectlySelectedRef(true);

        pointerDownScreen.x = event.clientX;
        pointerDownScreen.y = event.clientY;
        isDragEnabled = false;

        if (state.isTransformationActiveRef.current) {
            return;
        }

        if (
            event.button !== 0 ||
            !articleRef.current ||
            event.shiftKey ||
            state.isDraggingActive() ||
            state.isAutoDragActiveRef.current
        ) {
            return;
        }

        const currentArticle = state.articles[articleRef.current.id];
        const currentInstances = currentArticle?.articleInstances ?? articleRef.current.articleInstances;
        const { index, articleInstanceId } = getArticleInstanceData(event.instanceId, currentInstances);

        if (!articleInstanceId || index === undefined) {
            return;
        }

        const isSupportSelection = isSupportingConstruction(articleRef.current.articleNumber);

        if (isAnchorArticle(articleRef.current.articleNumber)) {
            const poiIndex = getPoiIndexForArticleInstance(
                state.pois,
                articleRef.current.id,
                articleInstanceId,
                currentInstances[articleInstanceId]?.poiGuids
            );
            if (poiIndex >= 0) {
                void state.prepareManualAnchorPlacement?.({
                    articleId: articleRef.current.id,
                    poiIndex
                });
            }
        }

        let showSelection = true;

        if (event.ctrlKey) {
            const instanceAlreadySelected = Object.keys(state.articleInstanceSelections).includes(articleInstanceId);
            showSelection = !instanceAlreadySelected;

            if (instanceAlreadySelected) {
                // Deselect the whole group (or just the single instance if not in a group)
                const groupMemberIds = isSupportSelection
                    ? [articleInstanceId]
                    : state.getGroupMemberInstanceIds(articleInstanceId);
                for (const memberId of groupMemberIds) {
                    state.removeArticleInstanceSelection(memberId);
                    instanceToArticleIdMap.delete(memberId);
                }

                let anyInstanceSelected = false;
                for (const articleId of instanceToArticleIdMap.values()) {
                    if (articleId === articleRef.current!.id) {
                        anyInstanceSelected = true;
                        break;
                    }
                }
                if (!anyInstanceSelected && !state.isBatchModeActive()) {
                    state.articleDragRefsMap.current.delete(articleRef.current.id);
                }

                showSelection = false;
            } else {
                // Add the clicked instance and all its group members to the selection
                const groupMemberIds = isSupportSelection
                    ? [articleInstanceId]
                    : state.getGroupMemberInstanceIds(articleInstanceId);
                for (const memberId of groupMemberIds) {
                    state.addArticleInstanceSelection(memberId, memberId === articleInstanceId ? index : undefined);
                    // Track articleId for each group member
                    const memberArticleId = getArticleId(memberId, state);
                    if (memberArticleId) {
                        instanceToArticleIdMap.set(memberId, memberArticleId);
                    }
                }

                // clone article instances for dragRef to ensure that zustand state doesn't get updated directly
                const dragRefClone = {
                    ...articleRef.current,
                    articleInstances: cloneArticleInstances(state.articles[articleRef.current.id])
                };

                state.articleDragRefsMap.current.set(articleRef.current.id, {
                    articleRef: { current: dragRefClone } as RefObject<ArticleReference | undefined>
                });
            }
        } else {
            const isAlreadySelected = Object.keys(state.articleInstanceSelections).includes(articleInstanceId);
            const groupMemberIds = isSupportSelection
                ? [articleInstanceId]
                : state.getGroupMemberInstanceIds(articleInstanceId);

            if (!isAlreadySelected) {
                instanceToArticleIdMap.clear();
                if (!state.isBatchModeActive()) {
                    state.articleDragRefsMap.current.clear();
                }

                // Expand selection to include all group members
                if (groupMemberIds.length > 1) {
                    const groupSelection: Record<string, number | undefined> = {};
                    for (const memberId of groupMemberIds) {
                        groupSelection[memberId] = undefined;
                    }
                    // Ensure the clicked instance has the correct index
                    groupSelection[articleInstanceId] = index;
                    state.setArticleInstanceSelections(groupSelection);
                } else {
                    state.setArticleInstanceSelections({ [articleInstanceId]: index });
                }
            }

            for (const memberId of groupMemberIds) {
                const memberArticleId = getArticleId(memberId, state);
                if (memberArticleId) {
                    instanceToArticleIdMap.set(memberId, memberArticleId);
                }
            }

            // clone article instances for dragRef to ensure that zustand state doesn't get updated directly
            const dragRefClone = {
                ...articleRef.current,
                articleInstances: cloneArticleInstances(state.articles[articleRef.current.id])
            };

            state.articleDragRefsMap.current.set(articleRef.current.id, {
                articleRef: { current: dragRefClone } as RefObject<ArticleReference | undefined>
            });
        }

        state.updateInstanceMatrices(showSelection, articleRef, false, [articleInstanceId]);

        if (showSelection) {
            snapAnchorInstanceId = articleInstanceId;

            if (event.point) {
                dragStartPoint.copy(event.point);
                hasDragStartPoint = true;
            } else if (event.ray?.intersectPlane(raycastPlane, dragStartPoint)) {
                hasDragStartPoint = true;
            }

            // pre-compute the dragging box for large selections
            const currentSelectedIds = Object.keys(get().articleInstanceSelections);
            if (currentSelectedIds.length > DRAGGING_BOX_THRESHOLD) {
                updateInstanceToArticleIdMap(currentSelectedIds, get());
                draggingBoxStatus.isPending = false;
                draggingBoxStatus.isComputed = false;

                scheduleDraggingBoxComputation(
                    currentSelectedIds,
                    articleInstanceId,
                    state.articleDragRefsMap.current,
                    state.draggingBoxRef.current,
                    state
                );
            } else {
                draggingBoxStatus.isComputed = false;
            }
        }
    },

    handleOnArticlePointerOver: (event, articleRef) => {
        if (event.shiftKey || get().isAutoDragActiveRef.current || get().isDraggingActive() || !articleRef.current) {
            return;
        }

        const state = get();
        const { index, articleInstanceId } = getArticleInstanceData(
            event.instanceId,
            articleRef.current.articleInstances
        );
        if (index === undefined || !articleInstanceId) {
            return;
        }

        // Highlight all group members if it belongs to a group
        const groupMemberIds = state.getGroupMemberInstanceIds(articleInstanceId);
        const idsToHighlight = groupMemberIds && groupMemberIds.length > 0 ? groupMemberIds : [articleInstanceId];

        // Collect refs by article for all group members
        const refsByArticle = new Map<string, HoverArticleGroupEntry>();
        const nextHoveredInstanceIds = new Set<string>();
        for (const memberId of idsToHighlight) {
            const memberArticleId = getArticleId(memberId, state);
            if (!memberArticleId) {
                continue;
            }
            const ref = resolveHoverArticleRef(state, memberArticleId, memberId, articleRef);
            if (!ref) {
                continue;
            }
            if (!refsByArticle.has(memberArticleId)) {
                refsByArticle.set(memberArticleId, { articleId: memberArticleId, ref, instanceIds: [memberId] });
            } else {
                refsByArticle.get(memberArticleId)!.instanceIds.push(memberId);
            }
            nextHoveredInstanceIds.add(memberId);
        }

        if (nextHoveredInstanceIds.size === 0) {
            clearHoveredHighlight(state);
            return;
        }

        if (
            nextHoveredInstanceIds.size === hoveredGroupInstanceIds.size &&
            Array.from(nextHoveredInstanceIds).every((id) => hoveredGroupInstanceIds.has(id))
        ) {
            return;
        }

        clearHoveredHighlight(state);

        // Set color highlight
        for (const { ref, instanceIds } of refsByArticle.values()) {
            state.updateInstanceMatrices(true, ref, false, instanceIds, undefined, instanceIds);
        }

        hoveredGroupByArticle.clear();
        for (const [key, value] of refsByArticle.entries()) {
            hoveredGroupByArticle.set(key, value);
        }

        hoveredGroupInstanceIds.clear();
        for (const id of nextHoveredInstanceIds) {
            hoveredGroupInstanceIds.add(id);
        }

        state.setHoveredArticleInstanceIds(Array.from(nextHoveredInstanceIds));

        const mainWebGLRendererRef = state.mainWebGLRendererRef;
        if (mainWebGLRendererRef.current && mainWebGLRendererRef.current.domElement.style.cursor !== 'move') {
            mainWebGLRendererRef.current.domElement.style.cursor = 'pointer';
        }
    },

    handleOnArticlePointerOut: (event, articleRef) => {
        if (event.shiftKey || get().isDraggingActive() || !articleRef.current) {
            get().setHoveredArticleInstanceIds([]);
            return;
        }

        const state = get();
        const { index, articleInstanceId } = getArticleInstanceData(
            event.instanceId,
            articleRef.current.articleInstances
        );

        if (index === undefined || !articleInstanceId) {
            return;
        }

        if (hoveredGroupInstanceIds.has(articleInstanceId)) {
            clearHoveredHighlight(state);
        }

        const mainWebGLRendererRef = state.mainWebGLRendererRef;
        if (mainWebGLRendererRef.current) {
            mainWebGLRendererRef.current.domElement.style.cursor = 'auto';
        }
    },

    clearHoveredArticleHighlight: () => {
        clearHoveredHighlight(get());
    },

    handleOnArticleContextMenu: () => {
        get().setIsContextMenuVisible(true);
    },

    handleOnUngroupSelection: () => {
        const state = get();
        const selectedIds = Object.keys(state.articleInstanceSelections);
        const groupIds = new Set<string>();

        for (const id of selectedIds) {
            const gid = state.instanceToGroupMap[id];
            if (gid) {
                groupIds.add(gid);
            }
        }
        for (const gid of groupIds) {
            state.removeArticleGroup(gid);
        }
    }
});
